import { useEffect, useSyncExternalStore } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { LiveEvent, ProjectChangedEvent } from '@pace/contracts';
import { applyProjectEvent } from '../data/projects';
import { API } from '../lib/api';
import { BOOTSTRAP_KEY } from '../data/bootstrap';
import { SESSION_KEY } from '../session';

type LiveState = 'off' | 'connecting' | 'open' | 'error';

let state: LiveState = 'off';
const listeners = new Set<() => void>();
const setState = (s: LiveState) => {
  state = s;
  listeners.forEach((fn) => fn());
};
const subscribe = (fn: () => void) => {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
};

export const useLiveStatus = () =>
  useSyncExternalStore(
    subscribe,
    () => state,
    () => state,
  );

/**
 * One Server-Sent-Events connection per signed-in tab. Pushes project changes into the
 * query cache so every screen reflects other departments' taps within seconds.
 */
export function useLiveStream(enabled: boolean): void {
  const qc = useQueryClient();
  useEffect(() => {
    if (!enabled || typeof EventSource === 'undefined') return;
    let es: EventSource | null = null;
    let retry: ReturnType<typeof setTimeout> | undefined;
    let closed = false;

    const connect = () => {
      setState('connecting');
      es = new EventSource(`${API}/stream`);
      es.addEventListener('open', () => setState('open'));
      es.addEventListener('hello', () => {
        setState('open');
        // Catch up on anything missed while disconnected.
        void qc.invalidateQueries({ queryKey: ['projects'] });
      });
      es.addEventListener('project.changed', (m) =>
        applyProjectEvent(qc, JSON.parse((m as MessageEvent).data) as ProjectChangedEvent),
      );
      es.addEventListener('masterdata.changed', (m) => {
        const e = JSON.parse((m as MessageEvent).data) as Extract<LiveEvent, { type: 'masterdata.changed' }>;
        void qc.invalidateQueries({ queryKey: BOOTSTRAP_KEY });
        void qc.invalidateQueries({ queryKey: ['admin', e.kind] });
      });
      es.addEventListener('session.revoked', () => {
        es?.close();
        void qc.invalidateQueries({ queryKey: SESSION_KEY });
      });
      es.addEventListener('error', () => {
        setState('error');
        // EventSource retries by itself unless the server answered with an error status.
        if (es?.readyState === EventSource.CLOSED && !closed) {
          retry = setTimeout(connect, 5_000);
          void qc.invalidateQueries({ queryKey: SESSION_KEY });
        }
      });
    };
    connect();
    return () => {
      closed = true;
      clearTimeout(retry);
      es?.close();
      setState('off');
    };
  }, [enabled, qc]);
}
